import { useMemo } from 'react';
import { useRouter } from '@/hooks';
import { Columns, TemplateTypeWithAllTab } from '@/types';
import {
  POI_COLUMNS,
  VIEW_3D_COLUMN,
} from '@/components/Analysis/Insights/InsightsExplorer/PointOfInterests/constants';
import {
  BLISTER_TYPE,
  PIT_TYPE,
  BLISTER_HEIGHT_LABEL,
  PIT_DEPTH_LABEL,
  ALL_POIS_TAB_LABEL,
} from '@/constants';
import { useAtomValue, useSetAtom } from 'jotai';
import { cameraTarget } from '@/state';
import { RowData } from '@/components/Analysis/Insights/InsightsExplorer/PointOfInterests/usePoiData';
import * as analysisState from '@/components/Analysis/state';
import { useBlisterFieldConfig } from './useBlisterFieldConfig';
import { mergeTemplateColumns } from '@/utils';

type UsePoiColumns = {
  selectedPoiTemplate: TemplateTypeWithAllTab;
  type?: string;
  include3DView?: boolean;
  hasVolumeOfInterest?: boolean;
};

export const usePoiColumns = ({
  selectedPoiTemplate,
  type,
  include3DView = false,
  hasVolumeOfInterest = false,
}: UsePoiColumns) => {
  const setCameraTarget = useSetAtom(cameraTarget);
  const setSelectedAssemblyId = useSetAtom(analysisState.selectedAssemblyId);
  const setSelectedAssemblyName = useSetAtom(analysisState.selectedAssemblyName);
  const customBlocksNames = useAtomValue(analysisState.customBlocksNames);
  const blisterFieldConfig = useBlisterFieldConfig({ type: type || '' });
  const router = useRouter();

  const allColumns = useMemo(() => {
    const columnsToRemove = new Set<string>();
    let commonColumnsWithType = [...POI_COLUMNS].filter((column) =>
      type ? column.key !== 'type' : true
    );

    // remove paintRegion column
    commonColumnsWithType = commonColumnsWithType.filter((column) => column.key !== 'paintRegion');

    // remove parentPaintRegionId column if no VolumeOfInterest templates exist
    if (!hasVolumeOfInterest) {
      commonColumnsWithType = commonColumnsWithType.filter(
        (column) => column.key !== 'parentPaintRegionId'
      );
    }

    const customBlockColumns = (
      name: string,
      sortKey?: string,
      key?: string,
      enableSorting?: boolean
    ) => ({
      isSortable: enableSorting ?? false,
      visibility: false,
      ...(sortKey ? { sortKey } : {}),
      key: key || name,
      columnName: name,
      displayConfig: { type: 'STRING' },
    });

    const customColumns = customBlocksNames.map((customBlock) =>
      customBlock === 'Paint Blocks'
        ? customBlockColumns(customBlock, 'templateFieldData.paint_block', 'paint_block', false)
        : customBlockColumns(customBlock)
    );

    commonColumnsWithType = [...commonColumnsWithType, ...customColumns];

    const blisterFields = (() => {
      const isBlisterOrPit =
        type?.toLowerCase().includes(BLISTER_TYPE.toLowerCase()) ||
        type?.toLowerCase().includes(PIT_TYPE.toLowerCase());

      if (!isBlisterOrPit) return [];

      const label =
        blisterFieldConfig.maxHeight?.name ||
        (type?.toLowerCase().includes(BLISTER_TYPE.toLowerCase())
          ? BLISTER_HEIGHT_LABEL
          : PIT_DEPTH_LABEL);

      const dynamicBlisterColumns: Columns[] = [];

      // Add maxHeight column if not hidden
      if (!blisterFieldConfig.maxHeight?.hidden) {
        dynamicBlisterColumns.push({
          isSortable: true,
          visibility: true,
          key: 'maxHeight',
          columnName: label,
          displayConfig: {
            type: 'NUMBER',
          },
          sortKey: 'blister.maxHeight',
        });
      }

      // Always show governing blister column
      dynamicBlisterColumns.push({
        visibility: true,
        isSortable: false,
        key: 'isGoverning',
        columnName: 'Governing Blister',
        displayConfig: {
          type: 'STRING',
        },
      });

      dynamicBlisterColumns.push({
        isSortable: true,
        visibility: true,
        key: 'measurementConfidence',
        columnName: 'Measurement Confidence',
        displayConfig: {
          type: 'STRING',
        },
        sortKey: 'blister.measurementConfidence',
      });

      // Add dynamic blister field columns based on template configuration
      if (blisterFieldConfig.wallLoss && !blisterFieldConfig.wallLoss.hidden) {
        dynamicBlisterColumns.push({
          isSortable: true,
          visibility: true,
          key: 'estimatedWallLoss',
          columnName: blisterFieldConfig.wallLoss.name,
          displayUnit: 'MILLIMETER',
          sortKey: 'blister.wallLoss.value',
        });
      }

      if (
        blisterFieldConfig.residualCorrosionAllowance &&
        !blisterFieldConfig.residualCorrosionAllowance.hidden
      ) {
        dynamicBlisterColumns.push({
          isSortable: true,
          visibility: true,
          key: 'residualCorrosionAllowance',
          columnName: blisterFieldConfig.residualCorrosionAllowance.name,
          displayUnit: 'MILLIMETER',
          sortKey: 'blister.residualCorrosionAllowance.value',
        });
      }

      if (blisterFieldConfig.residualThickness && !blisterFieldConfig.residualThickness.hidden) {
        dynamicBlisterColumns.push({
          isSortable: true,
          visibility: true,
          key: 'residualThickness',
          columnName: blisterFieldConfig.residualThickness.name,
          displayUnit: 'MILLIMETER',
          sortKey: 'blister.residualThickness.value',
        });
      }

      if (
        blisterFieldConfig.measurementConfidence &&
        !blisterFieldConfig.measurementConfidence.hidden
      ) {
        dynamicBlisterColumns.push({
          isSortable: true,
          visibility: true,
          key: 'measurementConfidence',
          columnName: blisterFieldConfig.measurementConfidence.name,
          sortKey: 'blister.measurementConfidence',
        });
      }
      if (blisterFieldConfig.distanceConfidence && !blisterFieldConfig.distanceConfidence.hidden) {
        dynamicBlisterColumns.push({
          isSortable: true,
          visibility: true,
          key: 'distanceConfidence',
          columnName: blisterFieldConfig.distanceConfidence.name,
          sortKey: 'blister.distanceConfidence',
        });
      }

      if (blisterFieldConfig.heightType && !blisterFieldConfig.heightType.hidden) {
        dynamicBlisterColumns.push({
          isSortable: false,
          visibility: true,
          key: 'heightType',
          columnName: blisterFieldConfig.heightType.name,
          displayConfig: {
            type: 'STRING',
          },
          sortKey: 'blister.heightType',
        });
      }

      if (blisterFieldConfig.estimatedHeight && !blisterFieldConfig.estimatedHeight.hidden) {
        dynamicBlisterColumns.push({
          isSortable: true,
          visibility: true,
          key: 'estimatedHeight',
          columnName: blisterFieldConfig.estimatedHeight.name,
          displayConfig: {
            type: 'STRING',
          },
          sortKey: 'blister.estimatedHeight',
        });
      }

      if (blisterFieldConfig.pdrHeight && !blisterFieldConfig.pdrHeight.hidden) {
        dynamicBlisterColumns.push({
          isSortable: true,
          visibility: true,
          key: 'pdrHeight',
          columnName: blisterFieldConfig.pdrHeight.name,
          displayConfig: {
            type: 'STRING',
          },
          sortKey: 'blister.pdrHeight',
        });
      }

      return dynamicBlisterColumns;
    })();

    const templateFields = (() => {
      const result = selectedPoiTemplate?.fields
        .map((field) => {
          let sortKey = field.key;
          if (field.defaultVisible) {
            if (field.key === 'paint_block') {
              sortKey = `templateFieldData.${field.key}`;
              columnsToRemove.add('paint_block');
            }
            if (field.key === 'deck') {
              sortKey = `templateFieldData.${field.key}`;
              columnsToRemove.add('deck');
            }
          }
          if (field.defaultVisible) {
            return {
              isSortable: field.displayConfig?.isSortable ?? false,
              visibility: true,
              sortKey: sortKey || field.id,
              key: field.key ?? field.id,
              columnName: field.name,
              displayConfig: {
                type: 'STRING',
              },
            };
          }
        })
        .filter((column) => !!column);

      if (!include3DView) return result as Columns[];

      const view3dColumn = {
        ...VIEW_3D_COLUMN,
        onCellClick: setSelectedAssemblyId
          ? (event: React.MouseEvent<HTMLDivElement, MouseEvent>, poi: RowData) => {
              event.stopPropagation();
              const { assemblyId, isUncontextualized, centerPoint3d, imageLink, fov, assembly } =
                poi;

              if (!isUncontextualized && centerPoint3d) {
                setCameraTarget({ lookAt: JSON.parse(centerPoint3d), fov });
              }

              setSelectedAssemblyId(assemblyId);
              setSelectedAssemblyName(assembly);
              const isCtrlClick = event.ctrlKey || event.metaKey;
              if (isCtrlClick) window.open(imageLink as string, '_blank');
              else router.push(imageLink.replace(window.location.origin, ''));
            }
          : undefined,
      };

      return [...result, view3dColumn] as Columns[];
    })();
    // Remove local columns if they exist in template columns
    let removeLocalColumns: Columns[] = commonColumnsWithType;
    if (columnsToRemove) {
      removeLocalColumns = commonColumnsWithType.filter(
        (column) => !columnsToRemove.has(column.key)
      );
    }
    const combined: Columns[] = [...removeLocalColumns, ...blisterFields, ...templateFields];

    if (selectedPoiTemplate.name === ALL_POIS_TAB_LABEL) {
      commonColumnsWithType = [
        ...(mergeTemplateColumns(selectedPoiTemplate, commonColumnsWithType) as Columns[]),
      ];
    }
    return { columns: combined, commonColumns: commonColumnsWithType };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPoiTemplate,
    type,
    customBlocksNames,
    blisterFieldConfig.maxHeight?.name,
    blisterFieldConfig.maxHeight?.hidden,
    blisterFieldConfig.wallLoss,
    blisterFieldConfig.residualCorrosionAllowance,
    blisterFieldConfig.residualThickness,
    include3DView,
    setSelectedAssemblyId,
    setSelectedAssemblyName,
    router,
    setCameraTarget,
    hasVolumeOfInterest,
  ]);

  const columns = useMemo(() => {
    return selectedPoiTemplate.name === ALL_POIS_TAB_LABEL
      ? allColumns?.commonColumns
      : allColumns?.columns;
  }, [allColumns?.columns, allColumns?.commonColumns, selectedPoiTemplate.name]);

  return columns;
};
